import Button from '@axa-japan/design-system-react/Button';
import Heading from '@axa-japan/design-system-react/Heading';
import { ArrowIcon, ErrorIcon } from '@axa-japan/design-system-react/Icons';
import RadioTileGroupField from '@axa-japan/design-system-react/RadioTileGroupField';
import Text from '@axa-japan/design-system-react/Text';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod';

import Badge from '@axa-japan/design-system-react/Badge';
import Message from '@axa-japan/design-system-react/Message';
import StatusModal from '@axa-japan/design-system-react/StatusModal';
import { useEffect, useState } from 'react';
import DocumentTable from '../components/DocumentTable';
import { useGetDocuments } from '../hooks/useGetDocuments';
import { DOCUMENT_STATUS } from '../types/session';
import { ValueOf } from '../utils/fileUtils';
import { DocumentType, FormDataType } from '../utils/formUtils';

export const UPLOAD_TYPE = {
  axaDevice: 'AXA_DEVICE',
  personalDevice: 'PERSONAL_DEVICE',
} as const;

export const GLOBAL_STATUS = {
  empty: 'EMPTY',
  incomplete: 'INCOMPLETE',
  completed: 'COMPLETED',
} as const;

type DocumentTableSelectionForm = {
  sessionId?: number;
  documentsSelected: string[];
  uploadType: string;
  pendingUploadDocuments: DocumentType[];
};

const DocumentSelectionPageSchema = z.object({
  documentsSelected: z.array(z.string()).min(1, { message: '表示されている書類の中から、書類を1つ以上選択してください。' }),
  uploadType: z.enum([UPLOAD_TYPE.axaDevice, UPLOAD_TYPE.personalDevice], {
    errorMap: () => ({ message: 'どちらかを選択してください。' }),
  }),
});

type DocumentTableSelectionProps = {
  initialData: DocumentTableSelectionForm;
  setFormData: React.Dispatch<React.SetStateAction<FormDataType>>;
  setStepper: React.Dispatch<React.SetStateAction<number>>;
  onNext: (data: DocumentTableSelectionForm) => void;
  onBack: () => void;
};

const CompleteStatusInfo = ({ status }: { status: ValueOf<typeof GLOBAL_STATUS> }) => {
  switch (status) {
    case GLOBAL_STATUS.completed:
      return (
        <Message title="選択した書類の撮影・送信、またはQRコード発行/SMS送信がすべて完了しました。" status="success">
          <ul className="list-disc ml-5">
            <li>
              “QRコード発行済またはSMS送信済”は、撮影・送信が完了したことをお客様にご確認ください。<br></br>
              お客様のご都合で後日の撮影・送信となる場合は、お客様のお手続きURLの有効期限が発行から72時間（3日間）であることをお客様へお伝えください。
            </li>
            <li>
              “送信エラー”と表示される書類は、再度、撮影・送信してください。 <br></br>
              繰り返し送信エラーとなる場合は、書面での提出をお願いいたします。
            </li>
          </ul>
          <h3 className="font-bold text-xl">ステータスを確認いただき、未送信の書類がなければ、そのまま画面を終了してください。</h3>
        </Message>
      );
    case GLOBAL_STATUS.incomplete:
      return (
        <Message title="一部の書類の撮影・送信、またはQRコード発行/SMS送信が完了していません。" status="warning">
          <ul className="list-disc ml-5">
            <li>ステータスをご確認の上、撮影・送信または撮影依頼を行ってください。</li>
            <li>
              ”送信エラー”と表示される書類については、再度、撮影・送信してください。
              <br />
              繰り返し送信エラーとなる場合は、書面での提出をお願いいたします。
            </li>
          </ul>
        </Message>
      );
    default:
      return <></>;
  }
};

const DocumentTableSelection: React.FC<DocumentTableSelectionProps> = ({ initialData, onNext, onBack, setFormData, setStepper }) => {
  const [globalFormStatus, setGlobalFormStatus] = useState<ValueOf<typeof GLOBAL_STATUS>>('EMPTY');
  const [showErrors, setShowErrors] = useState(false);
  const [isWarningModalOpen, setIsWarningModalOpen] = useState(false);
  const [isModalDocumentsOverwriteOpen, setIsModalDocumentsOverwriteOpen] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<DocumentTableSelectionForm>({
    resolver: zodResolver(DocumentSelectionPageSchema),
    mode: 'onSubmit',
    defaultValues: initialData,
  });

  const { data: documents } = useGetDocuments(initialData.sessionId as number);
  const latestRequestedDocuments =
    documents?.filter((item) => item.documentStatus === DOCUMENT_STATUS.urlGenerated || item.documentStatus === DOCUMENT_STATUS.smsSent) ?? [];
  useEffect(() => {
    const allDocumentsCompleted = documents?.every(
      ({ documentStatus }) =>
        documentStatus === DOCUMENT_STATUS.uploadCompleted ||
        documentStatus === DOCUMENT_STATUS.urlGenerated ||
        documentStatus === DOCUMENT_STATUS.smsSent,
    );
    const documentPartiallyCompleted =
      !allDocumentsCompleted &&
      documents?.find(
        (document) =>
          document.documentStatus === DOCUMENT_STATUS.uploadCompleted ||
          document.documentStatus === DOCUMENT_STATUS.urlGenerated ||
          document.documentStatus === DOCUMENT_STATUS.smsSent,
      );

    if (documentPartiallyCompleted) {
      setStepper(1);
      setGlobalFormStatus(GLOBAL_STATUS.incomplete);
    } else if (allDocumentsCompleted) {
      setGlobalFormStatus(GLOBAL_STATUS.completed);
      setStepper(3);
    } else {
      setGlobalFormStatus(GLOBAL_STATUS.empty);
      setStepper(1);
    }
    setFormData((prev) => ({
      ...prev,
      uploadType: '',
    }));
    setValue('uploadType', '');
  }, [documents]);

  const { documentsSelected } = initialData;
  const latestDocsIds = latestRequestedDocuments.map(({ id }) => id);

  const onSubmit: SubmitHandler<DocumentTableSelectionForm> = (formData) => {
    const { uploadType, documentsSelected: currentlySelected } = formData;
    const isOverwritingRequestedDocs =
      (uploadType === UPLOAD_TYPE.personalDevice && latestDocsIds.length > 0) ||
      (uploadType === UPLOAD_TYPE.axaDevice && latestDocsIds.some((doc) => currentlySelected.includes(doc)));

    if (isOverwritingRequestedDocs) {
      setIsModalDocumentsOverwriteOpen(true);
    } else onNext(formData);
  };

  const onError = () => {
    setShowErrors(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDocumentRowClick = (selectedRows: string[]) => {
    setFormData((prev) => ({ ...prev, documentsSelected: selectedRows }));
    return selectedRows;
  };

  const handleUploadTypeChange = (uploadType: string) => {
    setFormData((prev) => ({ ...prev, uploadType }));
    return uploadType;
  };

  const handleBack = () => {
    if (initialData.pendingUploadDocuments.length !== 0) setIsWarningModalOpen(true);
    else {
      setFormData((prev) => ({ ...prev, documentsSelected: [] }));
      onBack();
    }
  };

  return (
    <>
      <Heading as="h1">対象書類と撮影方法の組み合わせを選択してください</Heading>
      <CompleteStatusInfo status={globalFormStatus} />
      <StatusModal
        id="warning-overwriting-modal"
        title="発行済みQRコード/URLが無効になります"
        open={isModalDocumentsOverwriteOpen}
        setOpen={setIsModalDocumentsOverwriteOpen}
        content={
          <div className="flex flex-col gap-5">
            <Text>
              {initialData.uploadType === UPLOAD_TYPE.personalDevice
                ? 'QRコード/URLを新規発行するため、次の書類を含む発行済みQRコード/URLを無効にしますがよろしいでしょうか。'
                : 'お客さまに撮影依頼した書類を取扱者が撮影・送信する場合、次の書類を含む発行済みQRコード/URLを無効にしますがよろしいでしょうか。'}
            </Text>
            <ul className="list-disc ml-5">
              {latestRequestedDocuments.map((document) => {
                return (
                  <li key={`remaining${document.id}`}>
                    <Text>{document.name}</Text>
                  </li>
                );
              })}
            </ul>
            <Text>よろしければ、お客さまに発行済みQRコード/URLが無効になることをご連絡ください。</Text>

            {latestDocsIds.some((document) => !documentsSelected.includes(document)) && (
              <div className="flex flex-col gap-5">
                <Text>※QRコード/URLの無効化に伴い、次の書類の送信ステータスを未送信に戻します。</Text>
                <ul className="list-disc ml-5">
                  {latestRequestedDocuments
                    .filter(({ id }) => !documentsSelected.some((doc) => doc === id))
                    .map((document) => {
                      return <li key={`remaining${document.id}`}>{document.name}</li>;
                    })}
                </ul>
                <Text>
                  {initialData.uploadType === UPLOAD_TYPE.personalDevice
                    ? '未送信となった書類については、取扱者が撮影・送信してください。'
                    : '未送信となった書類については、再度お客さまに撮影依頼、または、取扱者が撮影・送信してください。'}
                </Text>
              </div>
            )}
          </div>
        }
        buttons={[
          {
            text: 'キャンセル',
          },
          {
            text: 'OK',
            onClick: () => onNext(initialData),
          },
        ]}
      />

      <StatusModal
        id="warning-delete-progress-modal"
        title="撮影したファイルはまだ送信されていません"
        open={isWarningModalOpen}
        setOpen={setIsWarningModalOpen}
        content="戻った場合、撮影・ファイル選択したファイルはすべて削除されますがよろしいでしょうか。"
        buttons={[
          {
            text: 'キャンセル',
          },
          {
            text: 'OK',
            onClick: () => {
              setFormData((prev) => ({ ...prev, documentsSelected: [], pendingUploadDocuments: [] }));
              onBack();
            },
          },
        ]}
      ></StatusModal>
      <form className="flex flex-col gap-10" onSubmit={handleSubmit(onSubmit, onError)}>
        {documents && (
          <div>
            <Heading as="h2" className={'pb-4'}>
              書類を選択する
            </Heading>
            {Boolean(errors.documentsSelected) && showErrors && (
              <Text color="danger" className="flex flex-row items-center">
                <ErrorIcon size="small"></ErrorIcon>
                {String(errors.documentsSelected?.message)}
              </Text>
            )}
            <Controller
              name={'documentsSelected'}
              control={control}
              render={({ field: { onChange } }) => (
                <DocumentTable
                  onSelectRow={(rows) => onChange(handleDocumentRowClick(rows))}
                  dataTable={documents}
                  selectedRows={documentsSelected}
                />
              )}
            />
          </div>
        )}

        <div className="flex flex-col gap-5">
          <Heading as="h2" className="flex gap-3 items-center">
            撮影方法を選択してください <Badge variant="danger" text="必須" />
          </Heading>
          <div className="flex flex-col">
            <Text>書類を撮影する方法を選択してください。</Text>
            <ul className="list-disc ml-5">
              <li>「お客様自身のデバイスで撮影」を選択いただくと、QRコードの読み取り、もしくはSMS送信するURLからお客さまにお手続きいただきます。 </li>
              <li className="font-bold">
                「このデバイスで撮影」はAXAタブレットのみ使用可能ですのでご注意ください。（背面カメラが無いPCの場合も選択不可です。）
              </li>
            </ul>
          </div>

          <Controller
            name={'uploadType'}
            control={control}
            render={({ field: { onChange } }) => (
              <RadioTileGroupField
                required={true}
                key={''}
                itemsPerRow={2}
                options={[
                  { title: 'お客様自身のデバイスで撮影', value: UPLOAD_TYPE.personalDevice },
                  { title: 'このデバイスで撮影', value: UPLOAD_TYPE.axaDevice },
                ]}
                onChange={(e) => onChange(handleUploadTypeChange(e.target.value))}
                showError={Boolean(errors.uploadType)}
                errorMessage={String(errors.uploadType?.message)}
              />
            )}
          />
        </div>

        <div className="flex justify-center gap-3 mt-5">
          <div className="w-80">
            <Button type="button" variant="outlined" onClick={handleBack} fullWidth icon={<ArrowIcon rotation="180" />} iconPosition="left">
              戻る
            </Button>
          </div>

          <div className="w-80">
            <Button type="submit" fullWidth icon={<ArrowIcon />}>
              次へ
            </Button>
          </div>
        </div>
      </form>
    </>
  );
};

export default DocumentTableSelection;
